import { useState, useCallback, useMemo, useEffect } from 'react';
import { cloneDeep } from 'lodash';

import { StepValidationStatuses } from 'enums';
import { getStepValidationStatus } from 'utils/formStepsUtils';

export type FormStep = {
  isValid: boolean;
  status: StepValidationStatuses;
};

export default function useFormSteps(
  defaultFormSteps: Array<FormStep>,
  defaultActiveStep: number,
) {
  const [formSteps, setFormSteps] = useState(defaultFormSteps);
  const [activeStep, setActiveStep] = useState(defaultActiveStep);

  useEffect(() => {
    setFormSteps(defaultFormSteps);
  }, [defaultFormSteps]);

  const onActiveStepChange = useCallback(
    (onStepChange: (step: number) => number) => {
      setActiveStep((oldActiveStep) => {
        const newActiveStep = onStepChange(oldActiveStep);
        const activeFormStep = formSteps[newActiveStep];
        const oldFormStep = formSteps[oldActiveStep];

        // Mark as finished only if step is valid and is before the active one
        if (oldFormStep.isValid && newActiveStep > oldActiveStep) {
          setFormSteps((oldFormSteps) => {
            const newFormSteps = cloneDeep(oldFormSteps);

            newFormSteps[oldActiveStep].status = StepValidationStatuses.Finish;

            return newFormSteps;
          });
        }

        // Active step should have Process status in case there were no form changes
        if (activeFormStep.status === StepValidationStatuses.Wait) {
          setFormSteps((oldFormSteps) => {
            const newFormSteps = cloneDeep(oldFormSteps);

            newFormSteps[newActiveStep].status = StepValidationStatuses.Process;

            return newFormSteps;
          });
        }

        return newActiveStep;
      });
    },
    [formSteps],
  );

  const handleFormChange = useCallback((formik: any, formStepKey: number) => {
    setFormSteps((oldFormSteps) => {
      const newStatus = getStepValidationStatus(formik);

      if (newStatus === oldFormSteps[formStepKey].status) return oldFormSteps;

      const newFormSteps = cloneDeep(oldFormSteps);

      newFormSteps[formStepKey].status = newStatus;

      newFormSteps[formStepKey].isValid =
        newStatus === StepValidationStatuses.Finish;

      // Step should have Process status in case there are no form errors
      if (newFormSteps[formStepKey].status !== StepValidationStatuses.Error) {
        newFormSteps[formStepKey].status = StepValidationStatuses.Process;
      }

      return newFormSteps;
    });
  }, []);

  const onFormChange = useMemo(() => {
    return formSteps.map(
      (formStep, index) => (formik: any) => handleFormChange(formik, index),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSteps = () => {
    setFormSteps(defaultFormSteps);
    setActiveStep(defaultActiveStep);
  };

  return [
    formSteps,
    onFormChange,
    activeStep,
    onActiveStepChange,
    resetSteps,
  ] as const;
}
